import { zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import { Agendamento, StatusHorario, StatusHorarioLabels } from '../../Enum';

export type ScheduleTone = 'blue' | 'orange' | 'green' | 'gray';

// Mesmo mapa de cores da agenda do estabelecimento.
export const getScheduleTone = (status: StatusHorario): ScheduleTone => {
    if (status === StatusHorario.CANCELADO) return 'gray';
    if (status === StatusHorario.PENDENTE) return 'orange';
    if (status === StatusHorario.CONCLUIDO) return 'green';
    return 'blue';
};

export const getScheduleStatusLabel = (agendamento: Agendamento) => {
    if (agendamento.status === StatusHorario.AGENDADO && agendamento.confirmation?.date) return 'Confirmado';
    return StatusHorarioLabels[agendamento.status] || agendamento.status;
};

export const formatBRL = (value?: number | string | null) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

export const getScheduleTotal = (agendamento: Agendamento) => {
    const value = [agendamento.totalPrice, agendamento.service?.price, (agendamento as any).price]
        .filter((item) => item !== undefined && item !== null && item !== '')
        .map((item) => Number(item))
        .find((item) => Number.isFinite(item));
    return value ?? null;
};

export const getServiceLabel = (agendamento: Agendamento) => {
    const extrasCount = agendamento.servicosExtras?.length || 0;
    const name = agendamento.service?.name || 'Serviço';
    return extrasCount ? `${name} +${extrasCount}` : name;
};

const isOpenStatus = (status: StatusHorario) => [StatusHorario.PENDENTE, StatusHorario.AGENDADO].includes(status);

// Proximos: pendentes/agendados que ainda nao terminaram. O resto vai para o historico.
export const isUpcomingSchedule = (agendamento: Agendamento, now = moment()) =>
    isOpenStatus(agendamento.status) && moment(agendamento.endAt).isAfter(now);

// Regras preservadas do card anterior.
export const canRequestConfirmation = (agendamento: Agendamento, now = moment()) => {
    const { confirmation, startAt, status } = agendamento;
    const deadline = moment(startAt).subtract(confirmation?.minutesBeforeConfirmation || 0, 'minutes');
    return Boolean(
        isOpenStatus(status) &&
            !confirmation?.date &&
            confirmation?.required &&
            confirmation?.minutesBeforeConfirmation &&
            now.isAfter(deadline),
    );
};

export const canRequestCancellation = (agendamento: Agendamento, now = moment()) => {
    const { cancellation, startAt, status } = agendamento;
    const deadline = now.clone().add(cancellation?.minutesBeforeCancellation || 0, 'minutes');
    return Boolean(
        isOpenStatus(status) &&
            !cancellation?.date &&
            cancellation?.required &&
            cancellation?.minutesBeforeCancellation &&
            moment(startAt).isAfter(deadline),
    );
};

// "Hoje", "Amanhã", "Sexta-feira" (proximos 6 dias) ou "Em 12 dias".
export const getRelativeDayLabel = (date: string | Date, now = moment(), timezone?: string) => {
    const target = zonedDate(date, timezone).startOf('day');
    const diff = target.diff(zonedDate(now, timezone).startOf('day'), 'days');
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    if (diff > 1 && diff < 7) return target.format('dddd');
    if (diff >= 7) return `Em ${diff} dias`;
    return target.format('D [de] MMMM');
};

export const groupByMonth = (agendamentos: Agendamento[]) => {
    const groups: { key: string; title: string; items: Agendamento[] }[] = [];
    agendamentos.forEach((agendamento) => {
        const start = zonedDate(agendamento.startAt, agendamento.timezone);
        const key = start.format('YYYY-MM');
        let group = groups.find((item) => item.key === key);
        if (!group) {
            const title = start.year() === zonedDate(new Date(), agendamento.timezone).year() ? start.format('MMMM') : start.format('MMMM [de] YYYY');
            group = { key, title, items: [] };
            groups.push(group);
        }
        group.items.push(agendamento);
    });
    return groups;
};
